import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  listWallets,
  getActiveWallet,
  createWallet,
  syncWallet,
  deleteWallet,
  requestTonProofChallenge,
  requestGenericTonProofChallenge,
  verifyTonProof,
} from "../../../shared/api/wallet";
import type { CreateWalletRequest } from "../../../shared/api/wallet";
import type { Wallet } from "../../../entities/wallet";
import { mapWalletResponseToEntity } from "../../../entities/wallet";
import { HttpError, isHttpError } from "../../../shared/api/http";

export const walletsKeys = {
  all: ["wallets"] as const,
  active: ["wallets", "active"] as const,
};

export const useWalletsQuery = (
  options?: Omit<UseQueryOptions<Wallet[], Error>, 'queryKey'>,
) =>
  useQuery<Wallet[], Error>({
    queryKey: walletsKeys.all,
    queryFn: async () => {
      const response = await listWallets();
      return response.map(mapWalletResponseToEntity);
    },
    ...options,
  });

export const useActiveWalletQuery = (
  options?: UseQueryOptions<Wallet | null, Error>,
) =>
  useQuery<Wallet | null, Error>({
    queryKey: walletsKeys.active,
    queryFn: async () => {
      try {
        const response = await getActiveWallet();
        return mapWalletResponseToEntity(response);
      } catch (error) {
        // Handle 404 gracefully - no active wallet is not an error
        if (isHttpError(error) && error.status === 404) {
          return null;
        }
        // Check for legacy error format (if error has status property)
        if (
          error &&
          typeof error === 'object' &&
          ('status' in error || 'response' in error)
        ) {
          const status = 'status' in error ? error.status : 
                        ('response' in error && error.response && typeof error.response === 'object' && 'status' in error.response) 
                          ? error.response.status : undefined;
          if (status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      if (isHttpError(error) && error.status === 404) {
        return false;
      }
      // Check for legacy error format
      if (
        error &&
        typeof error === 'object' &&
        ('status' in error || 'response' in error)
      ) {
        const status = 'status' in error ? error.status : 
                      ('response' in error && error.response && typeof error.response === 'object' && 'status' in error.response) 
                        ? error.response.status : undefined;
        if (status === 404) {
          return false;
        }
      }
      return failureCount < 3;
    },
    ...options,
  });

export const useCreateWalletMutation = (): UseMutationResult<
  Wallet,
  unknown,
  CreateWalletRequest
> => {
  const queryClient = useQueryClient();

  return useMutation<Wallet, unknown, CreateWalletRequest>({
    mutationFn: async (request) => {
      const response = await createWallet(request);
      return mapWalletResponseToEntity(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: walletsKeys.all });
    },
  });
};

export const useSyncWalletMutation = (): UseMutationResult<
  Wallet,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation<Wallet, unknown, string>({
    mutationFn: async (walletId) => {
      const response = await syncWallet(walletId);
      return mapWalletResponseToEntity(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: walletsKeys.all });
    },
  });
};

export const useDeleteWalletMutation = (): UseMutationResult<void, unknown, string> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: deleteWallet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: walletsKeys.all });
    },
  });
};

export const useRequestTonProofChallengeMutation = (): UseMutationResult<
  Awaited<ReturnType<typeof requestTonProofChallenge>>,
  unknown,
  string
> => {
  return useMutation({
    mutationFn: requestTonProofChallenge,
    retry: false, // Don't retry mutations - they should be explicitly triggered
  });
};

export const useRequestGenericTonProofChallengeMutation = (): UseMutationResult<
  Awaited<ReturnType<typeof requestGenericTonProofChallenge>>,
  unknown,
  void
> => {
  return useMutation({
    mutationFn: requestGenericTonProofChallenge,
    retry: false, // Don't retry mutations - they should be explicitly triggered
  });
};

export const useVerifyTonProofMutation = (): UseMutationResult<
  Wallet,
  unknown,
  { walletId: string; data: Parameters<typeof verifyTonProof>[1] }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ walletId, data }) => {
      const response = await verifyTonProof(walletId, data);
      return mapWalletResponseToEntity(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: walletsKeys.all });
    },
  });
};

// Keep the direct export for backward compatibility if needed
export { requestTonProofChallenge };
